import { useEffect, useRef, type ReactNode } from 'react';
import {
  Badge,
  Button,
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eureca/ui';
import {
  ConsultantCopyableId,
  ConsultantLayout,
  ConsultantListHeader,
  ConsultantListPlaceholder,
  ConsultantPageTitle,
  ConsultantSearchInput,
  ConsultantSortDropdown,
  ConsultantTablePagination,
  ConsultantTableSection,
  type ConsultantSortOption,
} from '@/shared/consultant-layout';
import {
  ArrowUpRightIcon,
  OctagonXIcon,
  PlusIcon,
  RadioIcon,
  SquarePenIcon,
  TriangleAlertIcon,
} from './icons';

export type CompanyStatus = 'active' | 'inactive';
export type ViewMode = 'cards' | 'table';

export interface Company {
  id: string;
  slug: string;
  name: string;
  status: CompanyStatus;
  logoUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Sort dropdown option as displayed to the user. The container builds these
 * from the canonical SORT_OPTIONS in `companies-state.ts` and passes them in.
 */
export interface SortOptionView {
  /** Stable id — the container uses `${sort}-${dir}` to disambiguate. */
  id: string;
  label: string;
  /** True when this option matches the currently active sort. */
  isSelected: boolean;
}

/** Allowed page sizes for the table view's "linhas por página" dropdown. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

/**
 * Inline refetch indicator mode. When set, the component renders a small banner
 * above the listing announcing the active refetch reason — used while data is
 * loading from a subsequent search/sort change (the full-page skeleton is for
 * cold/initial load only).
 *
 * - 'searching' → "Buscando empresas…" (active during search-driven refetch)
 * - 'sorting'   → "Reordenando empresas…" (active during sort-driven refetch)
 */
export type RefetchMode = 'searching' | 'sorting';

/**
 * Pagination state for the table view (classic page buttons).
 * Cards view uses scroll-infinite via `infinite` props instead.
 */
export interface PaginationProps {
  page: number;
  pageSize: PageSize;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: PageSize) => void;
}

/**
 * Infinite-scroll state for the cards view.
 * Cards do NOT show classic pagination — they extend on viewport intersection.
 */
export interface InfiniteScrollProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** Triggered when the sentinel enters the viewport. */
  onLoadMore: () => void;
}

export interface CompaniesPageProps {
  companies: Company[];
  view: ViewMode;
  /**
   * Server-reported total of in-scope companies (post-filter).
   * Lets the empty-state branch differentiate "scope is empty" (`Sem empresas`)
   * from "filter or out-of-range page returned nothing" (`Nenhum resultado encontrado`).
   */
  total: number;
  /**
   * Current page number from the URL — guards the cold-empty state so it only
   * shows on page 1 (per Figma + spec 025: page>1 with zero rows always means
   * "no results", never "scope is empty").
   */
  page: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** True when an active search filter is applied to the listing (URL-level). */
  hasActiveSearch?: boolean;
  /** Sort dropdown content built by the container from the canonical options. */
  sortOptions: SortOptionView[];
  /** Selected sort label shown next to the sort button (optional UX cue). */
  selectedSortLabel?: string;
  isSortSelected: boolean;
  onSelectSort: (id: string) => void;
  onClearSort: () => void;
  isTableSelected: boolean;
  onToggleView: () => void;
  onCreateCompany: () => void;
  onOpenCompany: (id: string) => void;
  onEditCompany: (id: string) => void;
  /** Id of the company/área de trabalho the consultant is currently operating
   *  in — its card gets an "Empresa selecionada" badge. */
  selectedCompanyId?: string;
  /** Logo node of the selected workspace, shown next to the "Empresas" title
   *  (replaces the generic factory glyph) when a company is entered. */
  titleBrand?: ReactNode;
  onLogout?: () => void;
  onHelp?: () => void;
  /** Required when `view === 'table'` — drives the pagination footer. */
  pagination?: PaginationProps;
  /** Required when `view === 'cards'` — drives the IntersectionObserver loader. */
  infinite?: InfiniteScrollProps;
  /** When set, renders the inline refetch banner ("Buscando…"/"Reordenando…"). */
  refetchMode?: RefetchMode;
  /**
   * EU-146 — disables the "Criar empresa" button when the consultor isn't in
   * exactly one squad (or admin). Defaults to enabled for stories that don't
   * pass the prop. When false, `createDisabledTooltip` is shown on hover.
   */
  canCreate?: boolean;
  createDisabledTooltip?: string;
}

export function CompaniesPage({
  companies,
  view,
  total,
  page,
  searchValue,
  onSearchChange,
  hasActiveSearch = false,
  sortOptions,
  selectedSortLabel,
  isSortSelected,
  onSelectSort,
  onClearSort,
  isTableSelected,
  onToggleView,
  onCreateCompany,
  onOpenCompany,
  onEditCompany,
  selectedCompanyId,
  titleBrand,
  onLogout,
  onHelp,
  pagination,
  infinite,
  refetchMode,
  canCreate = true,
  createDisabledTooltip,
}: CompaniesPageProps) {
  return (
    <ConsultantLayout activeMenuId="home" onLogout={onLogout} onHelp={onHelp}>
      <ConsultantLayout.Header>
        <ConsultantListHeader
          title={
            <ConsultantPageTitle icon={<FactoryGlyph />} brand={titleBrand}>
              Empresas
            </ConsultantPageTitle>
          }
          actions={
            <CreateCompanyButton
              canCreate={canCreate}
              tooltip={createDisabledTooltip}
              onClick={onCreateCompany}
            />
          }
          tools={
            <>
              <ConsultantSearchInput
                value={searchValue}
                onChange={onSearchChange}
                ariaLabel="Buscar empresas"
                placeholder="Busque por slug ou nome"
                data-testid="recruiter-companies-search"
              />
              <ConsultantSortDropdown
                ariaLabel="Ordenar empresas"
                activeAriaLabel={
                  selectedSortLabel ? `Ordenação atual: ${selectedSortLabel}` : undefined
                }
                options={sortOptions.map<ConsultantSortOption>((o) => ({
                  id: o.id,
                  label: o.label,
                  isActive: o.isSelected,
                }))}
                isActive={isSortSelected}
                onSelect={onSelectSort}
                onClear={onClearSort}
                data-testid="recruiter-companies-sort-toggle"
              />
              <Button
                variant="outline"
                size="icon"
                aria-label="Alternar visualização em tabela"
                aria-pressed={isTableSelected}
                data-state={isTableSelected ? 'selected' : 'default'}
                onClick={onToggleView}
                data-testid="recruiter-companies-view-toggle"
                className={cn(
                  isTableSelected &&
                    'border-primary bg-secondary text-primary hover:bg-secondary/80'
                )}
              >
                <TableGlyph />
              </Button>
            </>
          }
        />
      </ConsultantLayout.Header>

      <ConsultantLayout.Body>
        {refetchMode ? (
          // Per Figma (nodes 13193-2584 + sibling), search/sort refetch
          // replaces the listing area with a centered "Buscando…/Reordenando
          // empresas…" state — not an inline banner over the prior data.
          <RefetchBanner mode={refetchMode} />
        ) : companies.length === 0 ? (
          // "Sem empresas" only makes sense on page 1 with no filter and an
          // empty scope. Any other zero-row case (page out of range, filter
          // wiped the result set, search returned nothing) routes to
          // "Nenhum resultado encontrado".
          !hasActiveSearch && total === 0 && page === 1 ? (
            <EmptyState
              onCreateCompany={onCreateCompany}
              canCreate={canCreate}
              createDisabledTooltip={createDisabledTooltip}
            />
          ) : (
            <NoResultsState onClearSearch={() => onSearchChange('')} />
          )
        ) : view === 'table' ? (
          <CompaniesTableView
            companies={companies}
            onOpenCompany={onOpenCompany}
            onEditCompany={onEditCompany}
            pagination={pagination}
          />
        ) : (
          <CompaniesCardsView
            companies={companies}
            onOpenCompany={onOpenCompany}
            selectedCompanyId={selectedCompanyId}
            infinite={infinite}
          />
        )}
      </ConsultantLayout.Body>
    </ConsultantLayout>
  );
}

// ─── Cards view ──────────────────────────────────────────────────────────────

interface CompaniesCardsViewProps {
  companies: Company[];
  onOpenCompany: (id: string) => void;
  selectedCompanyId?: string;
  infinite?: InfiniteScrollProps;
}

interface CompaniesTableViewProps {
  companies: Company[];
  onOpenCompany: (id: string) => void;
  onEditCompany: (id: string) => void;
  pagination?: PaginationProps;
}

function CompaniesCardsView({
  companies,
  onOpenCompany,
  selectedCompanyId,
  infinite,
}: CompaniesCardsViewProps) {
  // Cards mode scrolls internally inside ConsultantLayout.Body so the page
  // wrapper stays locked at viewport height. Negative inline margins +
  // matching padding let the cards extend edge-to-edge while keeping the
  // scrollbar inside the body's padded region.
  return (
    <div className="-mx-4 flex min-h-0 flex-1 flex-col overflow-y-auto px-4 md:-mx-8 md:px-8">
      <section
        aria-label="Lista de empresas"
        data-testid="recruiter-companies-grid"
        className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
      >
        {companies.map((company) => (
          <CompanyCard
            key={company.id}
            company={company}
            isSelected={company.id === selectedCompanyId}
            onOpen={() => onOpenCompany(company.id)}
          />
        ))}
      </section>
      {infinite ? <InfiniteScrollSentinel {...infinite} /> : null}
    </div>
  );
}

/**
 * Sentinel element observed by IntersectionObserver. When ≥30% of it enters the
 * viewport AND there's a next page AND we're not already fetching, calls
 * `onLoadMore`. Renders an inline loading hint while a page is in flight
 * (FR-015 + FR-edge "scroll-loading indicator").
 */
function InfiniteScrollSentinel({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: InfiniteScrollProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  if (!hasNextPage && !isFetchingNextPage) return null;

  return (
    <div
      ref={ref}
      data-testid="recruiter-companies-scroll-sentinel"
      aria-busy={isFetchingNextPage}
      className="flex items-center justify-center py-8 text-sm text-muted-foreground"
    >
      {isFetchingNextPage ? 'Carregando mais empresas…' : ''}
    </div>
  );
}

interface CompanyCardProps {
  company: Company;
  /** Marks the card of the company the consultant is currently operating in. */
  isSelected?: boolean;
  onOpen: () => void;
}

/**
 * Workspace-style card matching Figma: 48×48 logo (or initial-letter
 * placeholder) + company name (text-2xl) + arrow-up-right indicator.
 *
 * Status / date / edit deliberately live in the table view — cards stay
 * minimal so they read as identity tiles, not data rows.
 */
function CompanyCard({ company, isSelected = false, onOpen }: CompanyCardProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Abrir empresa ${company.name}`}
      data-testid={`recruiter-companies-card-${company.slug}`}
      aria-current={isSelected ? 'true' : undefined}
      className={cn(
        'group flex min-h-52 flex-col justify-between gap-16 rounded-2xl border bg-card p-8 text-left',
        'shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)]',
        'transition-all hover:-translate-y-0.5 hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        // Selected workspace card is visually distinguished (ring + brand border).
        isSelected ? 'border-primary ring-1 ring-primary' : 'border-border'
      )}
    >
      <div className="flex flex-row items-start justify-between gap-4">
        {company.logoUrl ? (
          <img src={company.logoUrl} alt="" className="h-12 w-12 rounded-lg object-cover" />
        ) : (
          <div
            aria-hidden="true"
            className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-base font-medium text-foreground"
          >
            {company.name.charAt(0).toUpperCase()}
          </div>
        )}
        {isSelected ? (
          <Badge
            className="bg-secondary text-primary"
            data-testid={`recruiter-companies-card-${company.slug}-selected`}
          >
            Empresa selecionada
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-row items-end justify-between gap-4">
        <span className="text-2xl font-medium leading-none tracking-tight text-foreground">
          {company.name}
        </span>
        <ArrowUpRightIcon className="h-7 w-7 shrink-0 text-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </button>
  );
}

// ─── Table view ──────────────────────────────────────────────────────────────

function CompaniesTableView({
  companies,
  onOpenCompany,
  onEditCompany,
  pagination,
}: CompaniesTableViewProps) {
  return (
    <ConsultantTableSection
      ariaLabel="Tabela de empresas"
      data-testid="recruiter-companies-table"
      pagination={
        pagination ? (
          <ConsultantTablePagination
            page={pagination.page}
            totalPages={Math.max(1, Math.ceil(pagination.total / pagination.pageSize))}
            rowsPerPage={pagination.pageSize}
            rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
            onPageChange={pagination.onPageChange}
            onRowsPerPageChange={(size) => {
              // Narrow back to PageSize union — ConsultantTablePagination is generic
              // over numbers but our domain only allows the configured set.
              if ((PAGE_SIZE_OPTIONS as readonly number[]).includes(size)) {
                pagination.onPageSizeChange(size as PageSize);
              }
            }}
            data-testid="recruiter-companies-pagination"
          />
        ) : undefined
      }
    >
      <Table
        className="table-fixed"
        containerClassName="flex-1 overflow-auto"
        storageId="recruiter:companies-list"
      >
        <TableHeader className="sticky top-0 z-10 border-b border-border bg-card">
          <TableRow className="hover:bg-transparent">
            <TableHead resizable colKey="slug" className="w-[120px]">
              Slug
            </TableHead>
            <TableHead resizable colKey="name">
              Nome
            </TableHead>
            <TableHead resizable colKey="status">
              Status
            </TableHead>
            <TableHead resizable colKey="createdAt">
              Criada em
            </TableHead>
            <TableHead className="w-12">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {companies.map((company) => (
            <CompanyRow
              key={company.id}
              company={company}
              onOpen={() => onOpenCompany(company.id)}
              onEdit={() => onEditCompany(company.id)}
            />
          ))}
        </TableBody>
      </Table>
    </ConsultantTableSection>
  );
}

interface CompanyRowProps {
  company: Company;
  onOpen: () => void;
  onEdit: () => void;
}

function CompanyRow({ company, onOpen, onEdit }: CompanyRowProps) {
  return (
    <TableRow
      data-testid={`recruiter-companies-row-${company.slug}`}
      onClick={onOpen}
      tabIndex={0}
      role="link"
      aria-label={`Abrir empresa ${company.name}`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className="cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <TableCell>
        <ConsultantCopyableId value={company.slug} label="Slug" />
      </TableCell>
      <TableCell className="text-sm font-medium text-foreground">{company.name}</TableCell>
      <TableCell>
        <StatusBadge status={company.status} />
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        {formatShortDate(company.createdAt)}
      </TableCell>
      <TableCell className="text-right">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
          aria-label={`Editar ${company.name}`}
          data-testid={`recruiter-companies-edit-${company.slug}`}
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <SquarePenIcon className="size-4" />
        </button>
      </TableCell>
    </TableRow>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: CompanyStatus }) {
  if (status === 'active') {
    return (
      <Badge className="bg-secondary text-primary" leftIcon={<RadioIcon className="size-3" />}>
        Oportunidades ativas
      </Badge>
    );
  }
  return (
    <Badge
      className="bg-accent text-muted-foreground"
      leftIcon={<OctagonXIcon className="size-3" />}
    >
      Sem oportunidades
    </Badge>
  );
}

interface EmptyStateProps {
  onCreateCompany: () => void;
  canCreate: boolean;
  createDisabledTooltip?: string;
}

function EmptyState({ onCreateCompany, canCreate, createDisabledTooltip }: EmptyStateProps) {
  return (
    <ConsultantListPlaceholder data-testid="recruiter-companies-empty" className="gap-4">
      <div
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-muted"
      >
        <TriangleAlertIcon className="h-6 w-6 text-foreground" />
      </div>
      <h2 className="text-2xl font-semibold leading-none tracking-tight text-foreground">
        Sem empresas
      </h2>
      <p className="max-w-md text-sm text-muted-foreground">
        Sem nenhuma empresa cadastrada no momento. Cadastre uma para começar a gerenciar
        oportunidades.
      </p>
      <CreateCompanyButton
        canCreate={canCreate}
        tooltip={createDisabledTooltip}
        onClick={onCreateCompany}
      />
    </ConsultantListPlaceholder>
  );
}

/**
 * Full-area centered loading state shown while the listing silently refetches
 * after a search/sort change. Replaces the cards/table during the refetch per
 * Figma spec 025 (nodes 13193-2584 "Reordenando empresas…" + sibling for
 * "Buscando empresas…"). Distinct from the cold-load skeleton, which fires
 * only when there is no prior data at all.
 */
function RefetchBanner({ mode }: { mode: RefetchMode }) {
  const message = mode === 'searching' ? 'Buscando empresas…' : 'Reordenando empresas…';
  return (
    <ConsultantListPlaceholder
      role="status"
      aria-live="polite"
      data-testid={`recruiter-companies-refetch-${mode}`}
      className="gap-4 border-transparent bg-transparent"
    >
      <div
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-muted"
      >
        <span className="size-6 animate-spin rounded-full border-2 border-border border-t-foreground" />
      </div>
      <p className="text-2xl font-semibold leading-none tracking-tight text-foreground">
        {message}
      </p>
    </ConsultantListPlaceholder>
  );
}

interface NoResultsStateProps {
  onClearSearch: () => void;
}

/**
 * Distinct from `EmptyState` — rendered when there ARE companies in the system
 * but the active search filtered them all out. Offers "limpar busca" instead of
 * "criar empresa" because the user's intent here is to find an existing one.
 */
function NoResultsState({ onClearSearch }: NoResultsStateProps) {
  return (
    <ConsultantListPlaceholder
      data-testid="recruiter-companies-no-results"
      className="gap-4 border-transparent bg-transparent"
    >
      <div
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-muted"
      >
        <TriangleAlertIcon className="h-6 w-6 text-foreground" />
      </div>
      <h2 className="text-2xl font-semibold leading-none tracking-tight text-foreground">
        Nenhum resultado encontrado
      </h2>
      <p className="max-w-md text-sm text-muted-foreground">Tente buscar por outros termos.</p>
      <Button variant="outline" onClick={onClearSearch}>
        Limpar busca
      </Button>
    </ConsultantListPlaceholder>
  );
}

/** DD/MM/AA formatter for createdAt — matches the Figma table column. */
function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(d);
}

function FactoryGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden
    >
      <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M17 18h1" />
      <path d="M12 18h1" />
      <path d="M7 18h1" />
    </svg>
  );
}

function TableGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M3 9h18" />
      <path d="M3 15h18" />
      <path d="M9 3v18" />
      <path d="M15 3v18" />
    </svg>
  );
}

interface CreateCompanyButtonProps {
  canCreate: boolean;
  tooltip?: string;
  onClick: () => void;
}

/**
 * "Criar empresa" button + Tooltip wrapper for the multi-squad gate (EU-146).
 * Radix Tooltip cannot trigger on a disabled button, so when disabled we
 * render a focusable <span> wrapper that hosts the tooltip target.
 */
function CreateCompanyButton({ canCreate, tooltip, onClick }: CreateCompanyButtonProps) {
  if (canCreate || !tooltip) {
    return (
      <Button onClick={onClick} disabled={!canCreate} data-testid="recruiter-companies-create">
        <PlusIcon />
        Criar empresa
      </Button>
    );
  }
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} data-testid="recruiter-companies-create-disabled-wrapper">
            <Button disabled data-testid="recruiter-companies-create">
              <PlusIcon />
              Criar empresa
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
